import { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Container, ContainerContent, ContainerHeader } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Flex, colors } from 'styling/GlobalStyles'
import { Button } from 'components/ui/Button/Button'
import { BodyText, OtherText, TitleText } from 'styling/Typography'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { RootState } from 'store'
import useQueryPagination from 'hooks/useQueryPagination'
import { getKitchenIngredients } from 'features/food/foodSlice'
import { IKitchenIngredientResponse } from 'types/api/foodApi.interface'
import { IngredientsModal } from 'pages/Food/IngredientsModal'
import { IIngredientsModalState } from 'types/food.interface'

export const IngredientsTab = () => {
  const { kitchenIngredients, isLoading } = useSelector((store: RootState) => store.food)
  const [modalOpen, setModalOpen] = useState(false)
  const [editValue, setEditValue] = useState<IIngredientsModalState | undefined>()

  const params = useMemo(() => ({ page: 1, limit: 100 }), [])
  useQueryPagination({
    fetchFunctions: [getKitchenIngredients],
    cleanUpFunctions: [],
    params,
  })

  const columns: IHonestTableColumn<IKitchenIngredientResponse>[] = [
    {
      id: 'name',
      header: 'Ingredient',
      sticky: true,
      minWidth: '10rem',
      cell: (i) => (
        <Cell>
          <BodyText weight='600'>{i.name}</BodyText>
          {!i.skuLinkHealthy && (
            <OtherText color={colors.red}>SKU link unhealthy — missing or archived</OtherText>
          )}
        </Cell>
      ),
    },
    {
      id: 'sku',
      header: 'SKU link',
      cell: (i) =>
        i.sku ? (
          <StatusPill status={i.skuLinkHealthy ? 'accent' : 'danger'}>
            {i.sku.sku || i.sku.name}
          </StatusPill>
        ) : (
          <StatusPill status='danger'>Unlinked</StatusPill>
        ),
    },
    {
      id: 'unit',
      header: 'Recipe unit',
      cell: (i) => <BodyText>{i.recipeUnit?.name || '—'}</BodyText>,
    },
    {
      id: 'conversion',
      header: 'Stock → recipe factor',
      cell: (i) => <BodyText>{i.stockToRecipeFactor}</BodyText>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (i) => (
        <StatusPill status={i.active ? 'success' : 'accent'}>
          {i.active ? 'Active' : 'Archived'}
        </StatusPill>
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (i) => (
        <Flex justend width='100%'>
          <Button
            variant='secondary'
            size='sm'
            onClick={() => {
              setEditValue({
                id: i.id,
                name: i.name,
                sku: i.sku
                  ? { label: i.sku.name || i.sku.sku, value: i.sku.id }
                  : null,
                unit: i.recipeUnit
                  ? { label: i.recipeUnit.name, value: i.recipeUnit.id }
                  : null,
                stockToRecipeFactor: i.stockToRecipeFactor,
              })
              setModalOpen(true)
            }}
          >
            Edit
          </Button>
        </Flex>
      ),
    },
  ]

  return (
    <Container>
      <ContainerHeader padding>
        <Flex between center width='100%'>
          <TitleText weight='700'>Ingredients — linked to Inventory SKUs</TitleText>
          <Button
            variant='primary'
            size='sm'
            onClick={() => {
              setEditValue(undefined)
              setModalOpen(true)
            }}
          >
            New Ingredient
          </Button>
        </Flex>
      </ContainerHeader>
      <ContainerContent gap='0.625rem'>
        <HonestTable
          columns={columns}
          data={kitchenIngredients.data || []}
          keyField='id'
          loading={isLoading}
          emptyMessage='No ingredients yet'
        />
        <OtherText grey>
          Stock, batches, expiry and prices live in the Inventory ledger. skuLinkHealthy is false
          when the linked SKU is missing or archived.
        </OtherText>
      </ContainerContent>
      {modalOpen && (
        <IngredientsModal
          closeModal={() => {
            setModalOpen(false)
            setEditValue(undefined)
          }}
          value={editValue}
        />
      )}
    </Container>
  )
}
